/* eslint-disable max-lines-per-function */
import React, { Dispatch, useState } from 'react';
import browserHistory from 'services/history/browserHistory';
import t from 'utils/translationHelper';

import { Approver } from '@alpha/auth-dtos';
import { BeneficiaryDto } from '@alpha/bene-dtos';
import MenuDropdownItem from '@alpha/ui-lib/ui/atoms/MenuDropdownItem/MenuDropdownItem';
import { Box, ClickAwayListener, Typography } from '@alpha/ui-lib/ui/external';
import { ConfirmationModal } from '@alpha/ui-lib/ui/Modal/ConfirmationModal';
import APMenu from '@alpha/ui-lib/ui/molecules/Menu/Menu';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons';
import {
  faCircleCheck,
  faEdit, faEllipsisH, faShare, faTrashAlt, faXmarkCircle,
} from '@fortawesome/pro-regular-svg-icons';

import Authorization from '../../../../hocs/Authorization';
import useActionDropdown from '../../../../hooks/useActionDropDown';
import { UserRole } from '../../../../models/user';

import MultiSelectSection from './MultiSelectSection/MultiSelectSection';
import useStyles from './BeneficiaryActionDropDown.style';
import { Alert } from '@alpha/ui-lib/ui/Alert';
import BeneficiariesService from 'services/Beneficiaries/beneficiaries.service';
import useLog from 'hooks/useLog';
import useAlphaSnackbar from 'hooks/useAlphaSnackbar';

interface IProps {
  tabTitle: string;
  beneficiary?: BeneficiaryDto;
  displayMultiSelect?: boolean,
  currentOptions?: Approver[],
  setDisplayMultiSelect?: (val: boolean) => void,
  setCurrentOptions?: React.Dispatch<React.SetStateAction<Approver[]>>,
  setTextValue?: Dispatch<React.SetStateAction<string>>,
  handleReviewButton?: (param: BeneficiaryDto) => void,
  setSelectedBeneficiary?: Dispatch<React.SetStateAction<any>>,
  handleBeneficiaryShareButtonClick?: (bene?: BeneficiaryDto) => void,
  setDeleteModalOpen?: React.Dispatch<React.SetStateAction<false | 'reject' | 'delete'>>;
  handleApprovedEditButtonClick?: (clickedBeneficiary: BeneficiaryDto) => void,
  isExcludedFromMigration?: boolean,
  refetch?: () => void,
}

const BeneficiaryActionDropDown: React.FC<IProps> = ({
  beneficiary,
  displayMultiSelect,
  currentOptions,
  setDisplayMultiSelect,
  setCurrentOptions,
  setTextValue,
  handleReviewButton,
  setSelectedBeneficiary,
  tabTitle,
  handleBeneficiaryShareButtonClick,
  setDeleteModalOpen,
  handleApprovedEditButtonClick,
  isExcludedFromMigration = false,
  refetch,
}: IProps) => {
  const classes = useStyles();
  const [migrationModalOpen, setMigrationModalOpen] = useState(false);
  const [migrationToggleLoading, setMigrationToggleLoading] = useState(false);
  const sb = useAlphaSnackbar();
  const { logEvent, logError } = useLog();

  const handleConfirmMigrationToggle = async (menuBeneficiary: BeneficiaryDto): Promise<void> => {
    if (!handleToggleMigrationExclusion) {
      setMigrationModalOpen(false);
      return;
    }
    try {
      setMigrationToggleLoading(true);
      await handleToggleMigrationExclusion(menuBeneficiary);
    } finally {
      setMigrationToggleLoading(false);
      setMigrationModalOpen(false);
    }
  };

  const handleToggleMigrationExclusion = async (beneficiary: BeneficiaryDto) => {
    try {
      await BeneficiariesService.putUpdateBeneficiary(beneficiary.id, {
        ...beneficiary,
        corpayMigrationIgnore: !beneficiary.corpayMigrationIgnore,
      });
      sb.trigger(t('updated_beneficiary'), 'success');
      logEvent({ action: 'Successfully updated beneficiary' });
      refetch?.();
    } catch (e) {
      const errorMessage = (e.response?.data?.errors?.length
        && (e.response?.data?.errors[0])) || e.response?.data?.error || e.message || t('there_was_an_error_submitting_your_bene');
      sb.trigger(`${t('please_check_again')} ${errorMessage}`);
      logError({ action: 'Error updating beneficiary.', error: e });
    }
  };

  const {
    open,
    approvers,
    loading,
    menuCloseCount,
    setMenuCloseCount,
    displayIcon,
    submitApproverEmails,
    handleRenderDropdown,
    setOpen,
  } = useActionDropdown({
    currentOptions,
  });

  const handleClickAway = (): void => {
    if (menuCloseCount === 1) {
      setMenuCloseCount(menuCloseCount + 1);
    }
  };

  const handleClose = (): void => {
    if (setDisplayMultiSelect) { setDisplayMultiSelect(false); }
    setMenuCloseCount(0);
  };

  const styledText = (text: string) => (
    <span>
      <span style={{ fontWeight: 600 }}>{t('please_note^')}</span>
      {' '}
      <span style={{ fontWeight: 400 }}>{text}</span>
    </span>
  );

  const menuItems = (menuBeneficiary: BeneficiaryDto) => ({
    // PENDING
    pending: [{
      content: (
        <Typography data-testid={`review-bene-action-${menuBeneficiary.id}`}>
          {t('review')}
        </Typography>
      ),
      icon: displayIcon('View'),
      onClick: () => handleReviewButton && handleReviewButton(menuBeneficiary),
      underline: true,
      isReview: true,
    },
    {
      content: (
        <MultiSelectSection
          setMenuCloseCount={setMenuCloseCount}
          menuCloseCount={menuCloseCount}
          setOpen={setOpen}
          displayMultiSelect={displayMultiSelect}
          loading={loading}
          id={beneficiary.id}
          approvers={approvers}
          currentOptions={currentOptions}
          setCurrentOptions={setCurrentOptions}
          setTextValue={setTextValue}
          setDisplayMultiSelect={setDisplayMultiSelect}
          submitApproverEmails={submitApproverEmails}
          type="Beneficiary"
        />
      ),
      onClick: () => handleRenderDropdown(
        displayMultiSelect,
        menuBeneficiary.id,
        'Beneficiary',
        setDisplayMultiSelect,
      ),
      disableClick: true,
      icon: !displayMultiSelect ? faEnvelope : undefined,
      disableHoverEffect: displayMultiSelect,
    }],
    // APPROVED
    approved: [
      {
        content: (
          <Typography data-testid={`review-bene-action-${menuBeneficiary.id}`}>
            {t('update_information')}
          </Typography>
        ),
        icon: faEdit,
        onClick: () => {
          if (handleApprovedEditButtonClick) {
            handleApprovedEditButtonClick(menuBeneficiary);
          }
        },
        underline: true,
      },
      {
        content: (
          <Typography data-testid={`review-bene-action-${menuBeneficiary.id}`}>
            {t('share_beneficiary')}
          </Typography>
        ),
        icon: faShare,
        onClick: () => {
          if (setSelectedBeneficiary && tabTitle === 'approved') {
            setSelectedBeneficiary({
              menuBeneficiary,
              approved: tabTitle === 'approved',
            });
          }
          if (handleBeneficiaryShareButtonClick) {
            handleBeneficiaryShareButtonClick(
              menuBeneficiary,
            );
          }
        },
        underline: true,
      },
      {
        content: (
          <Typography data-testid={`review-bene-action-${menuBeneficiary.id}`}>
            {t('delete_beneficiary')}
          </Typography>
        ),
        icon: faTrashAlt,
        onClick: () => {
          if (setDeleteModalOpen) {
            setDeleteModalOpen('delete');
          }
        },
        underline: true,
        isDelete: true,
      },
      {
        content: (
          <Typography
            data-testid={`review-bene-action-${menuBeneficiary.id}`}
          >
            {isExcludedFromMigration
              ? t('include_in_migration')
              : t('exclude_from_migration')}
          </Typography>
        ),
        icon: isExcludedFromMigration ? faCircleCheck : faXmarkCircle,
        onClick: () => {
          setMigrationModalOpen(true);
        },
        underline: true,
      },
    ],
    // BATCH
    batch: [{
      content: (
        <Typography data-testid={`review-bene-action-${menuBeneficiary.id}`}>
          {t('view_job')}
        </Typography>
      ),
      icon: displayIcon('View'),
      onClick: () => {
        if (setSelectedBeneficiary) {
          browserHistory.push(`/app/beneficiaries/upload-summary/${menuBeneficiary.id}`);
        }
      },
    }],
  });

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        data-testid="action-bene-dropdown"
        className={classes.wrapper}
        onClick={(e) => {
          e.stopPropagation();
          if (setSelectedBeneficiary && tabTitle === 'approved') {
            setSelectedBeneficiary({
              beneficiary,
              approved: tabTitle === 'approved',
            });
          }
        }}
      >
        <ConfirmationModal
          variant={isExcludedFromMigration ? 'positive' : 'negative'}
          title={isExcludedFromMigration ? t('are_you_sure_you_want_to_include_this_beneficiary') : t('are_you_sure_you_want_to_exclude_this_beneficiary')}
          open={migrationModalOpen}
          disabled={migrationToggleLoading}
          onExit={() => setMigrationModalOpen(false)}
          onConfirm={() => {
            if (beneficiary) {
              handleConfirmMigrationToggle(beneficiary);
            }
          }}
          // @ts-ignore
          confirmIcon={isExcludedFromMigration ? faCircleCheck : faXmarkCircle}
          confirmButtonText={
            isExcludedFromMigration ? t('include_in_migration') : t('exclude_from_migration')
          }
          exitButtonText={t('cancel')}
          content={(
            <Alert variant={isExcludedFromMigration ? 'info' : 'warning'} text={styledText(isExcludedFromMigration
              ? t('including_this_beneficiary_will_allow_it_to_be_migrated')
              : t('excluding_this_beneficiary_will_prevent_it_from_being_migrated'))} />
          )}
          width="589px"
          bothButtons
          buttonSpacing="space-between"
          confirmButtonWidth={458}
          cancelButtonWidth={44}
        />
        <APMenu
          actions
          open={Boolean(open)}
          setOpen={setOpen}
          anchorEl={open}
          disableBackdropClick={menuCloseCount === 1}
          disableHoverEffect={displayMultiSelect}
          onClose={handleClose}
          buttonProps={{
            style: {
              height: 30,
              width: 30,
              minWidth: 0,
            },
          }}
          buttonTitle=""
          // @ts-ignore
          icon={faEllipsisH}
        >

          {
            menuItems(beneficiary)[tabTitle].map((item) => {
              if ((item.isReview && beneficiary.submittedById) || item.isDelete) {
                return (
                  <Authorization
                    requiredRoles={[[UserRole.BENEFICIARY_APPROVER,
                      UserRole.BENEFICAIRY_APPROVER_OWN]]}
                    resourceCreatorUserId={item.isReview ? beneficiary.submittedById : beneficiary.uploadedById}
                    fallback={<></>}
                  >
                    <MenuDropdownItem setOpen={setOpen}>{item}</MenuDropdownItem>
                  </Authorization>
                );
              }
              return <MenuDropdownItem setOpen={setOpen}>{item}</MenuDropdownItem>;
            })
          }
        </APMenu>
      </Box>
    </ClickAwayListener>
  );
};

export default BeneficiaryActionDropDown;
